/**
 * The questions asked before signing up.
 *
 * Separated from the landing page because the pre-render also reads them: these pairs are published
 * as `FAQPage` structured data, which is the shape a search engine lifts into an answer box. That
 * only stays honest while the questions marked up are the questions actually on the page, so both
 * render from this one list.
 *
 * The trial's terms here are the trial plan's features in the catalogue (one server, three monitors,
 * 25 Command Runner runs, one person, seven days). If an operator changes the trial plan, this answer
 * and `TRIAL` in pricing-data.ts change with it.
 */
export const LANDING_FAQ: { q: string; a: string }[] = [
  {
    q: 'Do you sell or host servers?',
    a: 'No. You connect servers you already have, from any provider, over SSH. Your account with your provider stays exactly as it is.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. Every new account starts with a 7-day free trial and no card: one server, three monitors and 25 Command Runner runs, with playbooks, templates, monitoring, Auto-Fix, backups and restore tests all included. Inviting other people needs a paid plan. There is no free plan after the trial.',
  },
  {
    q: 'What happens when the trial ends?',
    a: 'Nothing is deleted, and everything already on your server keeps running. Monitor checks, scheduled backups and restore tests pause, and you cannot start new runs until you choose a plan, which picks everything up where it stopped.',
  },
  {
    q: 'Does the platform need root access?',
    a: 'It can connect as root or as a user with sudo, with or without a sudo password, which you can store encrypted with the credential. It detects which applies when it checks the server, and tells you if an account cannot become root.',
  },
  {
    q: 'Which servers are supported?',
    a: 'Any Linux server you can reach over SSH can be connected and can run commands. Playbooks and templates support Ubuntu 22.04 and later, Debian 12 and later, and RHEL-compatible 8 and later (AlmaLinux, Rocky Linux, RHEL, CentOS Stream and Oracle Linux), and most also run on Amazon Linux 2023. Each one lists the exact versions it accepts. Other distributions, such as Fedora, openSUSE, Arch or Alpine, show as Unsupported OS: playbooks refuse them, while commands, monitoring and backups still work.',
  },
  {
    q: 'Can it set up a firewall and Fail2Ban?',
    a: 'Yes, when you run the Advanced Security playbook. It leaves a firewall the server already has alone, or turns on ufw or firewalld with SSH and the ports you choose open, adds a Fail2Ban jail for SSH, and puts back the settings it changed if any check fails. Auto-Fix never changes firewall rules.',
  },
  {
    q: 'How do I avoid breaking every server at once?',
    a: 'Every run is checked on each server first and can be dry-run. With a canary, one server goes first and the run waits for your confirmation before the rest continue in batches, which stop when failures pass the limit you set.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'Your servers keep running as they are, because nothing on them depends on the platform. Your plan runs until the end of the period you paid for; after that, automation, monitoring and backups stop, and backups already in your own storage stay there.',
  },
  {
    q: 'Can you see my data?',
    a: 'The platform stores what it needs to reach your servers, encrypted, plus run output, monitoring results and backup records. Backup archives go from your server to your own storage, and restore tests run on your server.',
  },
];
